import i18next from 'i18next';
import toast from 'react-hot-toast';
import { Close } from '@mui/icons-material';
import { fetchUserByTokenWithoutLoading } from '../../../../app/features/userSlice';
import { updateUser } from '../../../../services/userService';

// Helper: Render role-skill pairs
export const renderRoleSkills = (roleSkills, editMode, handleOptionRemove, styles, currentLanguage) => (
    roleSkills.map(roleSkill => (
        <div key={roleSkill.id} className={styles.roleSkillContainer}>
            <div className={styles.roleTitle}>
                {currentLanguage === 'en' 
                    ? (roleSkill.role_name_en || roleSkill.roleName) 
                    : (roleSkill.role_name_ar || roleSkill.roleName)}
            </div>
            <div className={styles.skillsList}>
                {roleSkill.skills.map((skill, index) => (
                    <span key={index} className={styles.skillTag}>
                        {skill}
                    </span>
                ))}
            </div>
            {/* Show role remove button in edit mode */}
            {!editMode && (
                <Close
                    className={styles.removeButton}
                    onClick={() => handleOptionRemove(roleSkill.role_id)}
                />
            )}
        </div>
    ))
);

// Helper function to process role-based skills data
export const processRoleBasedSkillData = (updatedData, user, options, formik, currentLanguage) => {
    if (updatedData) {
        formik.setFieldValue('roleBasedSkills', updatedData);
    } else if (user?.roleBasedSkills?.length > 0) {
        // Get unique role IDs to prevent duplicates caused by re-rendering
        const uniqueRoleIds = [...new Set(user.roleBasedSkills.map(rs => rs.role_id || rs.role))];
        
        // Map only unique roles
        const mappedRoleSkills = uniqueRoleIds.map(roleId => {
            // Find the first occurrence of this role in the user data
            const rs = user.roleBasedSkills.find(item => (item.role_id || item.role) === roleId);
            // Find the corresponding role in options to get language-specific data
            const roleOption = options.find(opt => opt.id === roleId);
            
            return {
                id: rs.id || Date.now(),
                role_id: roleId,
                role: roleId, // Keep for compatibility
                // Handle language-specific names
                role_name_en: rs.role_name_en || (roleOption ? roleOption.label_en : ''),
                role_name_ar: rs.role_name_ar || (roleOption ? roleOption.label_ar : ''),
                roleName: currentLanguage === 'en' 
                    ? (rs.role_name_en || (roleOption ? roleOption.label : '')) 
                    : (rs.role_name_ar || (roleOption ? roleOption.label : '')),
                skills: rs.skills
            };
        });
        formik.setFieldValue('roleBasedSkills', mappedRoleSkills);
    }
};

// Handler: Add new role-based skill
export const handleAddRoleBasedSkills = (values, options, formik, currentLanguage, handlePopupClose) => {
    const selectedRole = options.find(opt => opt.value === values.role);
    if (selectedRole) {
        const updatedData = [
            ...formik.values.roleBasedSkills,
            {
                id: Date.now(),
                role_id: selectedRole.id, // Store role ID
                role: selectedRole.id, // For backwards compatibility
                // Store both language versions
                role_name_en: selectedRole.label_en || selectedRole.label,
                role_name_ar: selectedRole.label_ar || selectedRole.label,
                // Current display name based on language
                roleName: currentLanguage === 'en' 
                    ? (selectedRole.label_en || selectedRole.label) 
                    : (selectedRole.label_ar || selectedRole.label),
                skills: values.skills
            }
        ];
        formik.setFieldValue('roleBasedSkills', updatedData);
        handlePopupClose();
    }
};

// Handler: Remove a role-based skill
export const handleOptionRemove = (roleSkillId, formik) => {
    const filteredSkills = formik.values.roleBasedSkills.filter(
        roleSkill => roleSkill.role_id !== roleSkillId
    );
    formik.setFieldValue('roleBasedSkills', filteredSkills);
};

// Function to handle form submission
export const handleFormSubmit = async (values, userToken, dispatch, formik, setEditMode, setFormLoading, t) => {
    if (values.formLoading) return;
    setFormLoading(true);
    const formData = new FormData();
    formData.append('token', userToken);
    // console.log(values,'values.roleBasedSkills')
    if (values.roleBasedSkills.length > 0) {
         values.roleBasedSkills.forEach((roleSkill, index) => {
            // Use role_id directly if it exists, otherwise find it from options
            const roleId = roleSkill.role_id || roleSkill.role;
            formData.append(`roleBasedSkills[${index}][role]`, roleId);
            if(roleSkill.skills.length > 0){    
            roleSkill.skills.forEach((skill, skillIndex) => {
                    formData.append(`roleBasedSkills[${index}][skills][${skillIndex}]`, skill);
                });
            }else{
                formData.append(`roleBasedSkills[${index}][skills]`, []);
            }
        });
    } else {
        formData.append('roleBasedSkills', '');
    }
       
    toast.promise(updateUser(formData), {
        loading: t('edit_profile.updating_profile'),
        success: <b>{t('edit_profile.profile_updated')}</b>,
        error: (err) => <b>{err.response.data.error}</b>,
    }).then(async () => {
        await dispatch(fetchUserByTokenWithoutLoading(userToken));
        setEditMode(prev => !prev);
        setFormLoading(false);
    }).catch((error) => {
        setFormLoading(false);
        if (error?.response?.data?.field) {
            formik.setErrors({
                [error.response.data.field]: error.response.data.error
            });
        }
    });
};

// Helper function to update role names on language change
export const updateRoleNamesOnLanguageChange = (formik, options, currentLanguage) => {
    const updatedSkills = formik.values.roleBasedSkills.map(rs => {
        const roleOption = options.find(opt => opt.id === (rs.role_id || rs.role));
        return {
            ...rs,
            roleName: currentLanguage === 'en' 
                ? (rs.role_name_en || (roleOption ? roleOption.label_en : '')) 
                : (rs.role_name_ar || (roleOption ? roleOption.label_ar : ''))
        };
    });
    formik.setFieldValue('roleBasedSkills', updatedSkills);
};

// Handle data loading and language changes
export const handleDataAndLanguageChanges = (options, user, initialLoadDone, formik, currentLanguage) => {
    // Only update the display names when language changes, not re-process everything
    if (options.length > 0 && user?.roleBasedSkills) {
        if (!initialLoadDone.current) {
            // First load - process everything
            processRoleBasedSkillData(null, user, options, formik, currentLanguage);
            initialLoadDone.current = true;
        } else {
            // Language change - just update display names
            updateRoleNamesOnLanguageChange(formik, options, currentLanguage);
        }
    }
}; 